// Sprint-V25 Issue L (Sa 12.09.2026) — Landing-Analytics-Client
// Feuert die vier definierten Landing-Page-Tracking-Events an das Backend
// POST /api/analytics/event. Fire-and-forget: Fehler brechen die UX nicht.
//
// Events:
//   hero_view      · hook_variant                            (Hero gerendert)
//   cta_click      · hook_variant · section                  (CTA angeklickt)
//   test_start     · hook_variant · utm_source               (Magic-Link angefordert)
//   test_complete  · hook_variant · fokustyp_result          (Ergebnis angezeigt)

import { api } from "@/lib/api";
import { HEADLINE_CANDIDATES } from "@/data/landingHookCopy";
import { isPreviewRuntime, getVisitSource } from "@/lib/trafficSource";
import { getHookAssignment } from "@/hooks/useHookVariant";

const VISITOR_COOKIE = "f4s_visitor_id";

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const raw = document.cookie || "";
  for (const part of raw.split(";")) {
    const [k, ...rest] = part.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("=") || "");
  }
  return null;
}

function writeCookie(name, value) {
  if (typeof document === "undefined") return;
  document.cookie =
    `${name}=${encodeURIComponent(value)}` +
    `; Max-Age=${60 * 60 * 24 * 365}` +
    `; Path=/` +
    `; SameSite=Lax`;
}

function ensureVisitorId() {
  const existing = readCookie(VISITOR_COOKIE);
  if (existing) return existing;
  const id =
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
    `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  writeCookie(VISITOR_COOKIE, id);
  return id;
}

function readUtmFromLocation() {
  if (typeof window === "undefined") return { utm_source: null, utm_content: null };
  const p = new URLSearchParams(window.location.search || "");
  return {
    utm_source: p.get("utm_source") || null,
    utm_content: p.get("utm_content") || null,
  };
}

export async function trackLandingEvent({
  event,
  hookVariant,
  section = null,
  fokustypResult = null,
  utmSource = null,
  utmContent = null,
  // Sprint V102.ab (02.10.2026) — Fragenset-A/B-Attribution.
  // Wenn gesetzt, wird der Wert als `utm_content`-Suffix mitgeschickt,
  // damit die Backend-Collection `landing_events` v1/v2 trennen kann,
  // ohne das Schema zu brechen.
  questionSet = null,
}) {
  try {
    const preview = isPreviewRuntime();
    const isCandidate = preview && !!HEADLINE_CANDIDATES[hookVariant];
    const previewFields = preview
      ? {
          traffic_source: getVisitSource().source,
          source_rule: getVisitSource().rule,
          hook_assignment: getHookAssignment(),
        }
      : {};
    // Neuer Mechanismus: keine Besucher-ID fuer Kandidaten-Events.
    const visitorId = isCandidate ? null : ensureVisitorId();
    const auto = readUtmFromLocation();
    const utmContentFinal = utmContent ?? auto.utm_content;
    // Hänge "qs_v1" / "qs_v2" an utm_content (fallback: pures Set), damit
    // die bestehende Aggregation ohne Schema-Change v1/v2 unterscheiden kann.
    const taggedUtmContent = questionSet
      ? [utmContentFinal, `qs_${questionSet}`].filter(Boolean).join("|")
      : utmContentFinal;
    await api.post("/analytics/event", {
      event,
      hook_variant: hookVariant,
      section,
      utm_source: utmSource ?? auto.utm_source,
      utm_content: taggedUtmContent,
      fokustyp_result: fokustypResult,
      visitor_id: visitorId,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      path: typeof window !== "undefined" ? window.location.pathname || null : null,
      ...previewFields,
    });
  } catch (_e) {
    // Fire-and-forget — Analytics-Fehler duerfen die UX nie stoeren.
  }
}

/** Baut einen UTM-suffigierten CTA-Href auf, damit das Ziel weiss, welche
 *  Variante den Klick gebracht hat. */
export function buildCtaHref(baseHref, hookVariant) {
  try {
    const u = new URL(baseHref, typeof window !== "undefined" ? window.location.origin : "http://x");
    u.searchParams.set("utm_source", "landing");
    u.searchParams.set("utm_content", `hook_${hookVariant}`);
    return u.pathname + (u.search || "") + (u.hash || "");
  } catch (_e) {
    return baseHref;
  }
}
